import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';

const MAX_RETRIES = 2;
const RETRY_DELAY = 1500;

const AuthCallback = () => {
  const navigate = useNavigate();
  const { exchangeSession, login } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Autenticando...');
  const [retryCount, setRetryCount] = useState(0);
  const [autoRetrying, setAutoRetrying] = useState(false);

  const triggerNewLogin = () => {
    sessionStorage.removeItem('used_sessions');
    localStorage.removeItem('session_token');
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processAuth = async (attempt = 0) => {
    try {
      const hash = window.location.hash || '';
      const search = window.location.search || '';
      const searchParams = new URLSearchParams(search);
      const hashParams = new URLSearchParams(hash.substring(1));

      // Check for OAuth error FIRST
      const oauthError = searchParams.get('error') || hashParams.get('error');
      if (oauthError) {
        const desc = searchParams.get('error_description') || hashParams.get('error_description') || '';
        console.warn('OAuth error:', oauthError, desc);

        // If user already has a valid session, just redirect
        const existingToken = localStorage.getItem('session_token');
        if (existingToken) {
          window.location.href = '/expenses';
          return;
        }

        // Auto-retry: start a fresh login flow automatically
        window.history.replaceState(null, '', '/');
        setStatus('Sessão expirada. Reconectando automaticamente...');
        setAutoRetrying(true);
        await new Promise(r => setTimeout(r, 1500));
        triggerNewLogin();
        return;
      }

      // Extract session_id
      let sessionId = null;
      if (hash.includes('session_id')) {
        sessionId = new URLSearchParams(hash.substring(1)).get('session_id');
      }
      if (!sessionId && search) {
        sessionId = searchParams.get('session_id');
      }

      if (!sessionId) {
        const existingToken = localStorage.getItem('session_token');
        if (existingToken) {
          window.location.href = '/expenses';
          return;
        }
        setError('Sessão não encontrada. Vamos tentar novamente.');
        // Auto-retry new login after a short delay
        await new Promise(r => setTimeout(r, 2000));
        triggerNewLogin();
        return;
      }

      // Prevent double-use of same session_id
      const usedSessions = JSON.parse(sessionStorage.getItem('used_sessions') || '[]');
      if (usedSessions.includes(sessionId)) {
        const token = localStorage.getItem('session_token');
        if (token) {
          navigate('/expenses', { replace: true });
        } else {
          // Session was used but login failed - start fresh
          triggerNewLogin();
        }
        return;
      }

      setStatus('Validando credenciais...');

      // Mark session as used
      usedSessions.push(sessionId);
      sessionStorage.setItem('used_sessions', JSON.stringify(usedSessions.slice(-10)));

      // Exchange session
      setStatus('Entrando na sua conta...');
      await exchangeSession(sessionId);

      setStatus('Login realizado com sucesso! Redirecionando...');

      const rawPath = localStorage.getItem('redirect_after_login');
      localStorage.removeItem('redirect_after_login');
      // Validate: must be a string starting with "/" - reject anything else (e.g. stale "[object Object]")
      const redirectPath = (typeof rawPath === 'string' && rawPath.startsWith('/')) ? rawPath : '/expenses';

      setTimeout(() => {
        window.location.href = redirectPath;
      }, 1000);

    } catch (err) {
      console.error('Auth error (attempt ' + attempt + '):', err.message, err.response?.status);

      // Remove from used sessions so retry can work
      const usedSessions = JSON.parse(sessionStorage.getItem('used_sessions') || '[]');
      sessionStorage.setItem('used_sessions', JSON.stringify(usedSessions.slice(0, -1)));

      // Auto-retry on server/network errors
      if (attempt < MAX_RETRIES && (!err.response || err.response.status >= 500 || err.code === 'ECONNABORTED')) {
        setRetryCount(attempt + 1);
        setStatus(`Tentando novamente (${attempt + 1}/${MAX_RETRIES})...`);
        await new Promise(r => setTimeout(r, RETRY_DELAY * (attempt + 1)));
        return processAuth(attempt + 1);
      }

      // Session expired or invalid (401) - auto trigger new login
      if (err.response?.status === 401) {
        setStatus('Sessão expirada. Reconectando...');
        setAutoRetrying(true);
        await new Promise(r => setTimeout(r, 1500));
        triggerNewLogin();
        return;
      }

      // Rate limited (429)
      if (err.response?.status === 429) {
        setError('Muitas tentativas. Aguarde 1 minuto e tente novamente.');
        return;
      }

      // All retries exhausted - show manual retry
      let msg = 'Erro ao fazer login. ';
      if (err.response?.status === 503) {
        msg = 'Serviço temporariamente indisponível. ';
      } else if (!err.response) {
        msg = 'Erro de conexão. Verifique sua internet. ';
      } else {
        msg = err.response?.data?.detail || 'Erro inesperado. ';
      }
      setError(msg);
    }
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    processAuth(0);
  }, []);

  if (autoRetrying) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#00FF94] animate-spin mx-auto mb-4" />
          <p className="text-gray-400">{status}</p>
          <p className="text-gray-600 text-sm mt-2">Você será redirecionado automaticamente...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center p-6 max-w-md">
          <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <p className="text-gray-300 mb-6">{error}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={triggerNewLogin}
              className="px-6 py-3 bg-[#00FF94] text-black rounded-full font-bold hover:bg-[#00DD7F] transition-colors flex items-center justify-center gap-2"
              data-testid="retry-login-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Tentar login novamente
            </button>
            <button
              onClick={() => { window.history.replaceState(null, '', '/'); navigate('/'); }}
              className="px-6 py-3 bg-gray-800 text-gray-300 rounded-full font-medium hover:bg-gray-700 transition-colors"
              data-testid="back-home-btn"
            >
              Voltar ao início
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-[#00FF94] animate-spin mx-auto mb-4" />
        <p className="text-gray-400">{status}</p>
        {retryCount > 0 && (
          <p className="text-gray-600 text-sm mt-2">Tentativa {retryCount + 1}...</p>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
